"use strict";
// packages/shared-react/src/hooks/useAgents.ts
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAgentApiAgentList = useAgentApiAgentList;
const react_1 = require("react");
const recoil_1 = require("recoil");
const state_1 = require("../state");
const shared_1 = require("@heartcheck/shared");
/**
 * IMPORTANT:
 * Use Cognito User Pools (ID token) through your /graphql proxy.
 * If you ever need private (IAM) mode instead, construct with the legacy boolean `false`.
 *   e.g., new GraphqlQuery(query, false)
 */
const listAgentsQuery = new shared_1.GraphqlQuery(
/* GraphQL */ `
    query ListAgents {
      listAgents {
        id
        name
        timestamp
        handlerLambda
        systemPrompt
        actions {
          id
          name
          type
          resource
          timestamp
        }
      }
    }
  `, { forwardAuthMode: "userpools" });
/**
 * Normalise any weird/legacy value into a proper Loadable<ObjRecord<Agent>>.
 * This protects us against undefined / plain-object states coming from older code.
 */
function normalizeAgentsState(raw) {
    if (!raw) {
        return state_1.Loadable.unloaded();
    }
    const candidate = raw;
    if (typeof candidate.isUnloaded === "function") {
        return candidate;
    }
    // If something non-Loadable got stored in the atom, fall back safely.
    return state_1.Loadable.unloaded();
}
/**
 * Backwards-compatible hook:
 *
 * - New code can do:
 *     const agentsLoadable = useAgentApiAgentList();
 *     if (agentsLoadable.isUnloaded()) ...
 *
 * - Old code can still do:
 *     const [agentsLoadable, refreshAgents] = useAgentApiAgentList();
 *
 * The returned value is an array with extra properties attached:
 *   result[0] === agentsState (Loadable<ObjRecord<Agent>>)
 *   result.refresh()  // reload
 *   result.value      // same as agentsState.value
 *   result.isUnloaded // bound method from Loadable
 */
function useAgentApiAgentList() {
    const [rawAgentsState, setAgentsState] = (0, recoil_1.useRecoilState)(state_1.Agents);
    const agentsState = normalizeAgentsState(rawAgentsState);
    const hasFetchedRef = (0, react_1.useRef)(false);
    const refresh = (0, react_1.useCallback)(() => {
        // Force a reload on next render
        hasFetchedRef.current = false;
        setAgentsState(state_1.Loadable.unloaded());
    }, [setAgentsState]);
    (0, react_1.useEffect)(() => {
        // Only fetch once per mount while state is unloaded
        if (hasFetchedRef.current || !agentsState.isUnloaded())
            return;
        hasFetchedRef.current = true;
        setAgentsState(state_1.Loadable.loading());
        listAgentsQuery
            .invoke()
            .then((value) => {
            const result = value;
            const list = Array.isArray(result?.listAgents)
                ? result.listAgents
                : [];
            if (list.length === 0) {
                // eslint-disable-next-line no-console
                console.warn("⚠️ No agents returned from API");
                setAgentsState(state_1.Loadable.loaded(state_1.ObjRecord.empty()));
                return;
            }
            // Prefer "agent-start" first, then alpha by name (fallback by id)
            const sorted = [...list].sort((a, b) => {
                if (a?.id === "agent-start")
                    return -1;
                if (b?.id === "agent-start")
                    return 1;
                const an = a?.name ?? a?.id ?? "";
                const bn = b?.name ?? b?.id ?? "";
                return an.localeCompare(bn);
            });
            setAgentsState(state_1.Loadable.loaded(state_1.ObjRecord.of(sorted)));
        })
            .catch((err) => {
            // eslint-disable-next-line no-console
            console.error("❌ Failed to fetch agents:", err);
            setAgentsState(state_1.Loadable.error(err));
        });
    }, [agentsState, setAgentsState]);
    // ----- Backwards-compatible return shape -----
    const current = normalizeAgentsState(rawAgentsState);
    const result = [current, refresh];
    // Object-style helpers for newer code
    result.state = current;
    result.value = current.value;
    result.loading = current.loading === "loading";
    // Our Loadable doesn't store error explicitly; keep the field for legacy callers.
    result.error = current.error ?? undefined;
    result.isUnloaded = current.isUnloaded.bind(current);
    result.refresh = refresh;
    return result;
}
